import { FC } from "react";
import { Card, Text, Table, Badge } from "@mantine/core";
import { useStyles } from "../Regtank.styles";
import { getRegtankColor } from "../../../../Utils/getRegtankDetails";
import type { RegtankData, RegtankLivenessData } from "../Regtank.types";
import {
  RegtankLivenessFHStatusEnum,
  RegtankOnboardingFHStatusEnum,
} from "@funded-here-interface/common/src/constant/enum";
import {
  USER_DATA_CARD_CONFIG,
  USER_DATA_CARD_HEADERS,
} from "../Regtank.constants";

interface UserDataCardProps {
  userData: RegtankData | RegtankLivenessData;
  type: "onboarding" | "liveness";
}

/**
 * Gets the status value from user data based on card type
 */
const getStatusValue = (
  userData: RegtankData | RegtankLivenessData,
  type: "onboarding" | "liveness"
): string | undefined => {
  const config = USER_DATA_CARD_CONFIG[type];
  return (userData as any)[config.statusKey];
};

/**
 * Checks if status badge should be displayed
 */
const shouldShowBadge = (
  status: string | undefined,
  type: "onboarding" | "liveness"
): boolean => {
  if (!status) return false;

  if (type === "liveness") {
    return status !== RegtankLivenessFHStatusEnum.NOTHING;
  }

  return true;
};

/**
 * Status badge component
 */
const StatusBadge: FC<{ status?: string; shouldShow: boolean }> = ({
  status,
  shouldShow,
}) => {
  if (!shouldShow || !status) return null;

  return (
    <Badge
      color={getRegtankColor(
        status as RegtankOnboardingFHStatusEnum | RegtankLivenessFHStatusEnum
      )}
    >
      {status}
    </Badge>
  );
};

/**
 * Displays user information card with verification status
 * Reusable for both onboarding and liveness modals
 */
export const UserDataCard: FC<UserDataCardProps> = ({ userData, type }) => {
  const { classes } = useStyles();
  const config = USER_DATA_CARD_CONFIG[type];
  const status = getStatusValue(userData, type);
  const show = shouldShowBadge(status, type);

  return (
    <Card
      withBorder
      shadow="sm"
      radius="md"
      className={classes.card}
      padding="md"
    >
      <Card.Section inheritPadding py="xs">
        <Text className={classes.subtext}>{userData.name}</Text>
        <Text className={classes.subSmallText}>{userData.userRole}</Text>
        <Text className={classes.subSmallText}>{userData.email}</Text>
      </Card.Section>
      <Card.Section inheritPadding py="xs">
        <Table withBorder withColumnBorders>
          <thead>
            <tr>
              <th className={classes.th}>{USER_DATA_CARD_HEADERS.status}</th>
              <th className={classes.th}>
                {USER_DATA_CARD_HEADERS.lastAttempt}
              </th>
              {config.showAttemptColumn && (
                <th className={classes.th}>
                  {USER_DATA_CARD_HEADERS.attempts}
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className={classes.td}>
                <StatusBadge status={status} shouldShow={show} />
              </td>
              <td className={classes.td}>{userData.lastAttemptDate}</td>
              {config.showAttemptColumn && (
                <td className={classes.td}>
                  {(userData as RegtankData).attemptUsed}
                </td>
              )}
            </tr>
          </tbody>
        </Table>
      </Card.Section>
    </Card>
  );
};
